/** @module @ignore */
import * as React from 'react';
import { Layer, MapMouseEvent, Source, useMap } from 'react-map-gl/mapbox';
import { FeatureCollection } from 'geojson';
import { featureCollection } from '@turf/helpers';

import { Mouse } from '@longline/aqua-ui/controls/Mouse';

import { useGlobalState } from '../contexts/state/GlobalStateContext';
import { PolygonUtils } from '../util/PolygonUtils';
import { IPoint } from '../types/IPoint';
import { IStructure } from '../types/IStructure';
import { Guide } from '../ui/Guide';
import { Key } from '@longline/aqua-ui/controls/Key';

/**
 * Overlay component for multi-selected structures.
 * Renders all selected structures with a highlight style and enables group dragging.
 */
const MultiStructureOverlay = () => {
  const globalState = useGlobalState();
  const map = useMap().current!;

  // Track dragging state
  const isDragging = React.useRef(false);
  const dragStart = React.useRef<IPoint | null>(null);

  // Generate GeoJSON for all selected structures
  const getJSON = React.useMemo((): FeatureCollection => {
    const features = globalState.selectedStructures.map((structure, idx) => {
      if (structure.type === 'circle') {
        return PolygonUtils.getCirclePolygon(structure.point, structure.radius);
      } else {
        const pts = structure.points.map(p => [p.lng, p.lat]);
        pts.push(pts[0]); // Close the polygon
        return {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'Polygon' as const,
            coordinates: [pts]
          }
        };
      }
    });

    return featureCollection(features);
  }, [globalState.selectedStructures, globalState.structures]);

  const handleMouseDown = (e: MapMouseEvent) => {
    if (!map) return;

    // Check if click is on one of the selected structures
    const features = map.queryRenderedFeatures(e.point, {
      layers: ['multi-select-fill']
    });

    if (features.length > 0) {
      e.preventDefault();
      isDragging.current = true;
      dragStart.current = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      map.getCanvas().style.cursor = 'grabbing';
      // Disable map panning during drag
      map.dragPan?.disable();
    }
  };

  const handleMouseMove = (e: MapMouseEvent) => {
    if (!isDragging.current || !dragStart.current) return;

    const dLng = e.lngLat.lng - dragStart.current.lng;
    const dLat = e.lngLat.lat - dragStart.current.lat;

    globalState.moveSelectedStructures(dLng, dLat);
    dragStart.current = { lng: e.lngLat.lng, lat: e.lngLat.lat };
  };

  const handleMouseUp = () => {
    if (isDragging.current) {
      isDragging.current = false;
      dragStart.current = null;
      if (map) {
        map.getCanvas().style.cursor = '';
        map.dragPan?.enable();
      }
    }
  };

  // Find which structure contains the clicked point
  const findClickedStructure = (clickedPoint: IPoint): IStructure | null => {
    for (const structure of globalState.selectedStructures) {
      let isInside = false;
      if (structure.type === 'circle') {
        const dist = PolygonUtils.distance(clickedPoint, structure.point);
        isInside = dist <= structure.radius;
      } else {
        const bbox = PolygonUtils.getBbox(structure.points);
        isInside = clickedPoint.lng >= bbox.getWest() && clickedPoint.lng <= bbox.getEast() &&
                   clickedPoint.lat >= bbox.getSouth() && clickedPoint.lat <= bbox.getNorth();
      }
      if (isInside) {
        return structure;
      }
    }
    return null;
  };

  const handleClick = (e: MapMouseEvent) => {
    if (!map) return;
    // Shift+click starts box-select, don't interfere
    if (e.originalEvent.shiftKey) return;
    // Don't handle click events during box-select mode
    if (globalState.mode === 'box-select') return;

    // Check if clicking on one of the selected structures
    const features = map.queryRenderedFeatures(e.point, {
      layers: ['multi-select-fill']
    });

    if (features.length > 0) {
      const clickedPoint = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      const clickedStructure = findClickedStructure(clickedPoint);

      if (clickedStructure) {
        const additive = e.originalEvent.ctrlKey || e.originalEvent.metaKey;
        if (additive) {
          // Ctrl+click: remove from selection
          globalState.toggleStructureSelection(clickedStructure, true);
        } else {
          // Regular click: select only the clicked structure
          globalState.setSelectedStructures([clickedStructure]);
        }
        e.preventDefault();
        return;
      }
    } else if (!e.defaultPrevented) {
      // Clicking outside the selected structures clears selection
      globalState.clearStructureSelection();
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    // Don't handle keyboard events during box-select mode
    if (globalState.mode === 'box-select') return;

    if (e.code === 'Escape' || e.code === 'Backspace') {
      globalState.clearStructureSelection();
    }
    if (e.code === 'Delete' || e.code === 'NumpadDecimal') {
      globalState.deleteSelectedStructures();
    }
  };

  React.useEffect(() => {
    if (!map) return;

    document.addEventListener('keydown', handleKeydown);
    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);
    map.on('click', handleClick);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
      map.off('click', handleClick);
      map.dragPan?.enable();
    };
  }, [map, globalState.selectedStructures]);

  return (
    <>
      {globalState.mode !== 'box-select' &&
        <Guide x={16} y={64} title="Multiple structures selected">
          <ul>
            <li><strong>{globalState.selectedStructures.length}</strong> structures selected.</li>
            <li>Click <Mouse size={14} left/> and drag to move.</li>
            <li><Key value="DEL"/> deletes all selected structures.</li>
            <li><Key value="ESC"/> clears selection.</li>
            <li><Key value="CTRL"/> <Key value="C"/>, <Key value="V"/> copies.</li>
          </ul>
        </Guide>
      }
      <Source type="geojson" data={getJSON}>
        <Layer
          id="multi-select-fill"
          type="fill"
          paint={{
            'fill-color': 'orange',
            'fill-opacity': 0.3
          }}
        />
        <Layer
          id="multi-select-line"
          type="line"
          paint={{
            'line-color': 'orange',
            'line-width': 2,
            'line-dasharray': [3, 2]
          }}
        />
      </Source>
    </>
  );
};

export { MultiStructureOverlay };
